import { amorphicStatic, property, remote, supertypeClass } from '@haventech/amorphic';
import { AppController } from '@hit/amorphic-types';
import { Request, Response } from 'express';

import { PassThrough } from 'stream';
import _ from 'lodash';

import { PolicyDoc } from '../../../common/js/customer/PolicyDoc';
import { PolicyAdminUtils } from '../../../common/js/policyAdmin/PolicyAdminUtils';
import { Policy } from '../../../common/js/customer/Policy';
import { Customer } from '../../../common/js/customer/Customer';
import { PolicyDocsController } from '../../../common/js/controller/PolicyDocsController';
import { Application, ApplicationSourceType } from '../../../common/js/customer/Application';
import { AuthenticatingController } from '../../../common/js/AuthenticatingController';
import { Utils } from '../../../common/js/Utils';
import { PdfController } from '../../../common/js/pdfUtil/PdfController';
import rp from 'request-promise-native';
import { Assumptions } from '../../../common/js/static/Assumptions';
import { MetadataApi } from '@hit/metadata-module';
import { PaymentInformation } from '../../../coverpath-common/classes/PaymentInformation';
import { AMCController } from '../../../common/js/authentication/amc/AMCController';
import { CoverpathUtils } from '../static/CoverpathUtils';
import { POLICY_DOC_ACTION_TYPE } from '../../../common/js/static/PolicyDocActionType';
import { PolicyDocAction } from '../../../common/js/customer/PolicyDocAction';
import { RevisedBasicIllustrationUtils } from '../../classes/wholelife/RevisedBasicIllustrationUtils';
import { CoverpathPolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { InitialInvoice } from '../../../common/js/policyAdmin/interface/InitialInvoiceInfoResponse';
import { resolve as pathResolve } from 'path';
import * as fs from 'fs';
import { CoverpathBasePolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathBasePolicy';
import { CoverpathWorkflowEmails } from '../../../common/js/workflow/CoverpathWorkflowEmails';
import { CoverpathAnnuity } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuity';
import { AnnuityPDFUtils, PDFMappingDataObject } from '../utility/annuity/pdf/AnnuityPDFUtils';
import { CoverpathUtilsBackEnd } from '../static/CoverpathUtilsBackEnd';
import { CoverpathCustomer } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { MMDirectCustomer } from '../../../coverpath-common/classes/mmdirect_subclasses/MMDirectCustomer';
import { CasApplication } from '../../../coverpath-common/classes/coverpath_subclasses/CasApplication';
import { MMDirectApplication } from '../../../coverpath-common/classes/mmdirect_subclasses/MMDirectApplication';
import { MMDirectPolicy } from '../../../coverpath-common/classes/mmdirect_subclasses/MMDirectPolicy';
import { serverOnly } from '../../../common/js/helpers';
import { InitialUIPayload, PolicyInfoPayload } from '@hit/policy-admin-types/dist/data-model/Types';
import {
  AnnuityAdditionalSigners,
  AnnuityAppDocument,
  CACPostRiderDTO,
  ConversionPaymentMode,
} from '../interfaces/CoverpathTypes';
import { ESignSessionMetadata } from '../../../common/js/customer/ESignSessionMetadata';
import { AnnuityDocuments } from '../static/annuity-app-package/AnnuityDocuments';
import { CashValueQuote, PolicyAdminClient } from '../../../common/js/policyAdmin/PolicyAdminClient';
import { RiderType } from '../../../common/js/static/Product';
import { PolicyRider } from '../../../common/js/customer/PolicyRider';
import { Exchange1035 } from '../../../common/js/quote/coverpath/Exchange1035';
import { HtmlPdfUtils } from '../../../coverpath-common/classes/wholelife/HtmlPdfUtils';
import { createHmac } from 'crypto';
import { TELE_CASE_STATUS } from '../../../coverpath-agent/public/js/types/Types';
import { CommonUtilsBackend } from '../../../common/js/static/CommonUtilsBackend';
import { POLICY_TYPES } from '../../../common/js/constants/policy';
import {
  POLICY_DOC_ANNUITY_DOCTYPE,
  POLICY_DOC_DOCTYPE,
  POLICY_DOC_SOURCES,
  POLICY_DOC_TYPES,
} from '../../../common/js/constants/policy-doc';
import { IllustrationService } from '../../../coverpath-common/classes/wholelife/IllustrationService';
import { IllustrationRequest } from '../../../coverpath-common/classes/wholelife/IllustrationRequest';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../common/js/logger/LoggerSingleton';
import { SecurityUtils } from '../static/SecurityUtils';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../common/js/logger/UILoggerSingleton';
import { ClientReapplyResponseType } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { PolicyAdminRiders } from '../../../common/js/policyAdmin/PolicyAdminRiders';

@supertypeClass({ toClient: true, toServer: true })
export abstract class CoverpathBaseAccountCenterController extends AppController {
  abstract getPath(): string;

  //TODO - We should remove calls to this for full multiple policy support
  //https://massmutual.atlassian.net/browse/HL-19070
  abstract getApplication(): CasApplication | MMDirectApplication;

  abstract getController(): AuthenticatingController;

  abstract getAMCController(): AMCController;

  abstract getCustomer(): CoverpathCustomer | MMDirectCustomer;

  abstract getAppLevelAmorphicTransaction();

  @property({ getType: () => PaymentInformation })
  paymentInformation: PaymentInformation;

  @property()
  giactWebDebitResponseCode: string;

  @property()
  isGiactWebDebitValid: boolean;

  @property()
  lastWebDebitAttemptCombination: string;

  @property()
  longPoll: number;

  @property()
  shortPoll: number;

  @property()
  actionStatusPoll: number;

  @property()
  delay: number;

  @property()
  policyDocsController: PolicyDocsController;

  @property({})
  initialInvoice: number;

  @property({})
  tlicToKOEsign: boolean = false;

  initPolicyDocsController(): void {
    if (!this.policyDocsController) {
      this.policyDocsController = PolicyDocsController.new(this.getController());
    }
  }

  @remote({ on: 'server' })
  setupPollConfigs() {
    this.longPoll = this.amorphic.config.nconf.get('accountCenterLongPoll');
    this.shortPoll = this.amorphic.config.nconf.get('accountCenterShortPoll');
    this.actionStatusPoll = this.amorphic.config.nconf.get('accountCenterActionStatusPoll');
    this.delay = this.amorphic.config.nconf.get('accountCenterSyncDelay');
  }

  protected getPolicyWithNumber(policyNumber: string): CoverpathPolicy | MMDirectPolicy {
    const applications = this.getCustomer().activeApplications;

    for (const application of applications) {
      if (application.primaryPolicy.policyNumber === policyNumber) {
        return application.primaryPolicy;
      }

      if (application.secondaryPolicy && application.secondaryPolicy.policyNumber === policyNumber) {
        return application.secondaryPolicy;
      }

      if (application.primaryPolicyWithPendingAmendment.policyNumber === policyNumber) {
        return application.primaryPolicyWithPendingAmendment;
      }

      if (
        application.secondaryPolicyWithPendingAmendment &&
        application.secondaryPolicyWithPendingAmendment.policyNumber === policyNumber
      ) {
        return application.secondaryPolicyWithPendingAmendment;
      }
    }

    throw Error(`Cannot find policy ${policyNumber}`);
  }

  async checkAcceptApplicationReq(policy: Policy): Promise<void> {
    const futureSubState = 'Waiting for Signature';
    await this.checkRequirement(policy, 'Accept Application', futureSubState);
    if (policy.isAccelerated()) {
      await this.setAcceptOfferDate(policy);
    }
  }

  @remote({ on: 'server' })
  async excludeJointFromOR1(policy): Promise<boolean> {
    return CoverpathUtils.isGoingToOR1(policy);
  }

  @remote({ on: 'server' })
  async regenerateESignLink(policy: CoverpathBasePolicy): Promise<void> {
    await policy
      .getOwnerSigningPerson()
      .esignMetadata.setEmbedUrl(
        policy.owner.email,
        policy.getOwnerSigningName(),
        policy.application.applicationNumber
      );
  }

  async acceptOffer(policy: CoverpathBasePolicy): Promise<void> {
    let futureState = policy.futureWorkflowState;
    let futureSubState = policy.futureWorkflowSubState;
    const excludeJointFromOR1 = await this.excludeJointFromOR1(policy);

    if (!excludeJointFromOR1 && !CoverpathUtils.shouldGenerateInsuredEsignMetadata(policy)) {
      futureState = 'Application';
      futureSubState = 'Issuing Policy';
    }
    await this.setAcceptOfferDate(policy);
    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathBaseAccountCenterController',
        function: 'acceptOffer',
        data: {
          policyNumber: policy.policyNumber,
          ...Utils.extractIds(policy, policy.application.customer),
        },
        category: UICategory.request,
      },
      'accept offer'
    );
    await this.checkRequirement(policy, 'Accept Application', futureSubState, futureState);
  }

  async acceptOfferPreview(policy: CoverpathPolicy): Promise<void> {
    const futureSubState = 'Waiting for 1035 Funds';
    const futureState = 'Funding Pause';

    await this.setAcceptOfferPreviewDate(policy);
    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathBaseAccountCenterController',
        function: 'acceptOfferPreview',
        data: {
          policyNumber: policy.policyNumber,
          ...Utils.extractIds(policy, policy.application.customer),
        },
        category: UICategory.request,
      },
      'accept offer preview'
    );
    await this.checkRequirement(policy, 'Accept Offer Preview', futureSubState, futureState);
  }

  @remote({ on: 'server' })
  async setAcceptOfferDate(policy: Policy): Promise<void> {
    policy.application.finalOfferAcceptedDate = Utils.now();
  }

  @remote({ on: 'server' })
  async setAcceptOfferPreviewDate(policy: CoverpathPolicy): Promise<void> {
    policy.application.previewOfferAcceptedDate = Utils.now();
  }

  resendDocusign(policy: Policy): void {
    const futureSubState = 'Waiting for Signature';
    this.checkRequirement(policy, 'Resend Application to Docusign', futureSubState, null);
  }

  async cancelApplication(policy: Policy): Promise<void> {
    const futureState = 'Application Canceled By User';
    const futureSubState = 'Without TLIC';
    return this.checkRequirement(policy, 'Cancel', futureSubState, futureState);
  }

  @remote({ on: 'server' })
  async declineTlicOffer(policyNumber: string): Promise<void> {
    const policy = this.getPolicyWithNumber(policyNumber);
    const futureState = 'Waiting for Signature';
    const futureSubState = 'Haven Application';
    this.tlicToKOEsign = true;
    await this.checkRequirement(policy, 'TLIC Offer Turned Down', futureState, futureSubState);
  }

  @serverOnly()
  private async getPolicyAdminStatusAttribute(
    policy: Policy,
    attributeAccessor: string
  ): Promise<boolean | string | Date> {
    if (!policy) {
      return false;
    }

    const paStatus = await PolicyAdminUtils.getPolicyAdminStatus(policy);

    if (!paStatus) {
      return false;
    }

    switch (attributeAccessor) {
      case 'isPolicyHeld':
        return paStatus.isPolicyHeld();
      case 'specialHandling':
        return paStatus.specialHandling();
      case 'getWorkflowEquivalentStage':
        return paStatus.getWorkflowEquivalentStage();
      case 'getGraceEndDate':
        return paStatus.getGraceEndDate();
      case 'getPolicyStageCode':
        return paStatus.getPolicyStageCode();
      case 'isPAPolicy':
        return paStatus.isPAPolicy();
      case 'getWorkflowEquivalentStageForCoverpath':
        return paStatus.getWorkflowEquivalentStageForCoverpathAgent();
      default:
        return false;
    }
  }

  @remote({ on: 'server' })
  public async getPAStatusEndGraceDate(policyNumber: string): Promise<Date> {
    const policy = this.getPolicyWithNumber(policyNumber);
    const graceEndDate = (await this.getPolicyAdminStatusAttribute(policy, 'getGraceEndDate')) as Date;
    return graceEndDate;
  }

  @remote({ on: 'server' })
  public async getPAStatusIsPolicyHeld(policyNumber: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber);
    const isPolicyHeld = (await this.getPolicyAdminStatusAttribute(policy, 'isPolicyHeld')) as boolean;
    return isPolicyHeld;
  }

  @remote({ on: 'server' })
  public async getPAStatusSpecialHandling(policyNumber: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber);
    const isPolicyHeld = (await this.getPolicyAdminStatusAttribute(policy, 'specialHandling')) as boolean;
    return isPolicyHeld;
  }

  @remote({ on: 'server' })
  public async isPAPolicy(policyNumber: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber);
    const isPAPolicy = (await this.getPolicyAdminStatusAttribute(policy, 'isPAPolicy')) as boolean;
    return isPAPolicy;
  }

  @remote({ on: 'server' })
  public async getPAStatusWorkflowEquivalentStage(policyNumber: string): Promise<string> {
    const policy = this.getPolicyWithNumber(policyNumber);
    const workflowEquivalentStage = await this.getPolicyAdminStatusAttribute(policy, 'getWorkflowEquivalentStage');

    if (!workflowEquivalentStage) {
      return '';
    } else {
      return workflowEquivalentStage as string;
    }
  }

  @remote({ on: 'server' })
  public async getPAStatusWorkflowEquivalentStageForCoverpath(policyNumber: string): Promise<string> {
    const policy = this.getPolicyWithNumber(policyNumber);
    const workflowEquivalentStage = await this.getPolicyAdminStatusAttribute(
      policy,
      'getWorkflowEquivalentStageForCoverpath'
    );

    if (!workflowEquivalentStage) {
      return '';
    } else {
      return workflowEquivalentStage as string;
    }
  }

  @remote({ on: 'server' })
  async isCanceledInPA(policyNumber: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber);
    const policyStageCode = await this.getPolicyAdminStatusAttribute(policy, 'getPolicyStageCode');

    if (!policyStageCode) {
      return false;
    } else {
      return /^TM/.test(policyStageCode as string);
    }
  }

  @remote({ on: 'server' })
  async wasPACanceledDeclinedRejected(policyNumber: string): Promise<boolean> {
    const stage = await this.getPAStatusWorkflowEquivalentStage(policyNumber);

    if (stage) {
      return /Canceled|Rejected|Declined/.test(stage);
    } else {
      return false;
    }
  }

  @remote({ on: 'server' })
  public async getPAPolicyInfoBatch(policyNumbers: Array<string>): Promise<Array<PolicyInfoPayload>> {
    const policyInfo = await PolicyAdminUtils.getPAPolicyInfoBatch(policyNumbers);
    return policyInfo;
  }

  @remote({ on: 'server' })
  public async getPAPayerInfo(policy: Policy) {
    const paPayerInfo = await PolicyAdminClient.getPayerInfo(policy);
    LoggerSingleton.getInstance().info(
      {
        module: 'CoverpathBaseAccountCenterController',
        activity: 'getPAPayerInfo',
        category: Category.request,
      },
      `Fetched payer data from policy admin for policy number ${
        policy && policy.policyNumber ? policy.policyNumber : ''
      }`
    );
    return paPayerInfo;
  }

  @remote({ on: 'server' })
  public async getPAPolicy(policy: Policy): Promise<InitialUIPayload> {
    const paPolicy = await PolicyAdminClient.getPolicy(policy);
    LoggerSingleton.getInstance().info(
      {
        module: 'CoverpathBaseAccountCenterController',
        function: 'getPAPolicy',
        data: { policyNumber: policy.policyNumber },
        category: Category.request,
      },
      `Fetched data from policy admin for policy number ${policy && policy.policyNumber ? policy.policyNumber : ''}`
    );
    return paPolicy;
  }

  @remote({ on: 'server' })
  public async getInsuranceSegmentInfo(policy: Policy) {
    const insuranceSegmentInfo = await PolicyAdminClient.getInsuranceSegmentInfo(policy);
    LoggerSingleton.getInstance().info(
      {
        module: 'CoverpathBaseAccountCenterController',
        function: 'getInsuranceSegmentInfo',
        data: { insuranceSegmentInfo },
        category: Category.request,
      },
      `Fetched segment info from policy admin for policy number ${
        policy && policy.policyNumber ? policy.policyNumber : ''
      }`
    );
    return insuranceSegmentInfo;
  }

  @remote({ on: 'server' })
  public async getPAInvoices(policy: Policy) {
    const invoicesInfo = await PolicyAdminClient.getInvoicesInfo(policy);
    LoggerSingleton.getInstance().info(
      {
        module: 'CoverpathBaseAccountCenterController',
        function: 'getInvoicesInfo',
        data: { invoicesInfo },
        category: Category.request,
      },
      `Fetched invoices info from policy admin for policy number ${
        policy && policy.policyNumber ? policy.policyNumber : ''
      }`
    );
    return invoicesInfo;
  }

  getViewApplicationURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewapplication=true`;
  }

  getViewPolicyAdminDocsURL(policyType: string): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&policyAdminDocs=true&policyType=${policyType}`;
  }

  getViewPolicyAdminAnnualStatementURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewAnnualStatement=true`;
  }

  getViewAmendmentsFormURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewAmendmentsForm=true`;
  }

  getViewInsurabilityFormURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewInsurabilityForm=true`;
  }

  getViewMecFormURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewMecForm=true`;
  }

  getViewRevisedBasicIllustrationURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewRevisedBasicIllustration=true`;
  }

  getViewAnnuityDocURL(appType: string): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewAnnuityDoc=true&appType=${appType}`;
  }

  getViewConversionPdfURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewConversionForm=true`;
  }

  getViewPappPdfURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewPappForm=true`;
  }

  getViewConsentPdfURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewConsentForm=true`;
  }

  getViewBasicIllustrationURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewBasicIllustration=true`;
  }

  isAccountCenterQueryString(query): boolean {
    return (
      query.policyAdminDocs ||
      query.viewapplication ||
      query.viewAmendmentsForm ||
      query.viewInsurabilityForm ||
      query.viewRevisedBasicIllustration ||
      query.viewMecForm ||
      query.viewAnnualStatement ||
      query.viewAnnuityDoc ||
      query.viewConversionForm ||
      query.viewPappForm ||
      query.viewConsentForm ||
      query.viewBasicIllustration
    );
  }

  @remote({ on: 'server' })
  async onContentRequestHandler(query, request: Request, response: Response): Promise<void> {
    if (!this.getController().loggedIn) {
      response.writeHead(302, { Location: '/account-center' });
      response.end();
      return Promise.resolve();
    }

    //TODO: Change when we enter a multiple applications world
    //TODO: https://massmutual.atlassian.net/browse/HL-19070
    let policy;

    if (query.policyType === 'secondary') {
      policy = this.getApplication().secondaryPolicy;
    } else {
      policy = this.getApplication().primaryPolicy;
    }

    if (query.policyAdminDocs) {
      this.initPolicyDocsController();
      await this.policyDocsController.getPolicyDocs(policy);

      if (this.policyDocsController.policyDocsInPA) {
        LoggerSingleton.getInstance().info(
          {
            module: 'CoverpathBaseAccountCenterController',
            function: 'onContentRequestHandler',

            data: Utils.extractIds(policy),
            category: Category.request,
          },
          'Received request to open policy packet'
        );

        await this.policyDocsController.openLatestPolicyDoc(response, !query.download);
        return Promise.resolve();
      } else {
        const error = {
          isHumanRelated: false,
          data: Utils.extractIds(policy),
        };
        LoggerSingleton.getInstance().error(
          {
            module: 'CoverpathBaseAccountCenterController',
            function: 'onContentRequestHandler',

            category: Category.request,
            error: error,
          },
          'Received request to open policy docs, but they are not in PA'
        );
        return Promise.reject('Received request to open policy docs, but they are not in PA');
      }
    } else if (query.viewAnnualStatement) {
      this.initPolicyDocsController();
      await this.policyDocsController.getPolicyAnnualStatement(policy);
      LoggerSingleton.getInstance().info(
        {
          module: 'CoverpathBaseAccountCenterController',
          function: 'onContentRequestHandler',
          context: {
            piiLevel: PiiLevelEnum.s2,
          },
          data: Utils.extractIds(policy),
          category: Category.request,
        },
        'Received request to view annual statement'
      );
      await this.policyDocsController.openLatestPolicyDoc(response);
      return Promise.resolve();
    }

    let docAsBuffer = null;
    let fileName = null;
    try {
      if (query.viewapplication) {
        docAsBuffer = await this.getApplicationPDFAsBuffer(policy);
        fileName = `Application-${policy.policyNumber}`;
      } else if (query.viewAmendmentsForm) {
        const formName = CoverpathUtils.getAmendmentFormNameByState(policy.getApplicationState());
        const amendmentsPolicyDoc = await this.getSinglePolicyDoc(policy, 'Amendment', formName);

        if (amendmentsPolicyDoc) {
          docAsBuffer = docAsBuffer = Buffer.from(amendmentsPolicyDoc.docContent.toString(), 'base64');
        }

        fileName = `Amendments-${policy.policyNumber}`;
      } else if (query.viewInsurabilityForm) {
        const formName = CoverpathUtils.getInsurabilityFormNameByState(policy.getApplicationState());
        const insurabilityPolicyDoc = await this.getSinglePolicyDoc(policy, 'Amendment', formName);
        if (insurabilityPolicyDoc) {
          docAsBuffer = Buffer.from(insurabilityPolicyDoc.docContent.toString(), 'base64');
        }

        fileName = `InsurabilityStatements-${policy.policyNumber}`;
      } else if (query.viewRevisedBasicIllustration) {
        const rbiDoc = await this.getSinglePolicyDoc(
          policy,
          POLICY_DOC_DOCTYPE.RevisedBasicIllustration,
          POLICY_DOC_DOCTYPE.RevisedBasicIllustration
        );

        if (rbiDoc) {
          docAsBuffer = Buffer.from(rbiDoc.docContent.toString(), 'base64');
        }

        fileName = `RevisedBasicIllustration-${policy.policyNumber}`;
      } else if (query.viewMecForm) {
        const mecDoc = await this.getSinglePolicyDoc(policy, 'Amendment', 'a5030_mec');

        if (mecDoc) {
          docAsBuffer = Buffer.from(mecDoc.docContent.toString(), 'base64');
        }

        fileName = `Mec-${policy.policyNumber}`;
      } else if (query.viewConversionForm) {
        //conversion always uses ownerState, we want to avoid look up for applicationSate as we are setting owner address from conversion quote.
        //For sanderling this defaults to insuredState as we are missing data when we check for isInsuredSameAsOwner
        const formName = CoverpathUtils.getConversionFormNameByState(policy.ownerPersonResidentialAddress.state);
        const conversionDoc = await this.getSinglePolicyDoc(policy, 'Conversion', formName);
        if (conversionDoc) {
          docAsBuffer = Buffer.from(conversionDoc.docContent.toString(), 'base64');
        }

        fileName = `Conversion-${policy.policyNumber}`;
      } else if (query.viewPappForm) {
        const formName = CoverpathUtils.getPappFormNameByState(policy.getApplicationState());
        const pappDoc = await this.getSinglePolicyDoc(policy, 'Papp', formName);
        if (pappDoc) {
          docAsBuffer = Buffer.from(pappDoc.docContent.toString(), 'base64');
        }

        fileName = `Papp-${policy.policyNumber}`;
      } else if (query.viewConsentForm) {
        const formName = CoverpathUtils.getConsentFormNameByState(policy.getApplicationState());
        const consentDoc = await this.getSinglePolicyDoc(policy, 'Consent', formName);
        if (consentDoc) {
          docAsBuffer = Buffer.from(consentDoc.docContent.toString(), 'base64');
        }

        fileName = `Consent-${policy.policyNumber}`;
      } else if (query.viewBasicIllustration) {
        const basicIllustration = await this.getSinglePolicyDoc(
          policy,
          POLICY_DOC_DOCTYPE.basicIllustration,
          'BasicIllustration'
        );
        if (basicIllustration) {
          docAsBuffer = Buffer.from(basicIllustration.docContent.toString(), 'base64');
        }

        fileName = `Ba-${policy.policyNumber}`;
      } else if (query.viewAnnuityDoc) {
        const { appType } = query;
        const policyDoc = await this.getSinglePolicyDoc(policy, appType, appType);
        if (policyDoc) {
          docAsBuffer = Buffer.from(policyDoc.docContent.toString(), 'base64');
        }

        fileName = `AnnuityDoc-${appType}-${policy.policyNumber}`;
      }
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message,
        stack: err.stack,
      };

      LoggerSingleton.getInstance().error(
        {
          module: 'CoverpathBaseAccountCenterController',
          function: 'onContentRequestHandler',

          category: Category.request,
          error: error,
        },
        `Encountered error when getting document for policy ${policy.policyNumber}`
      );

      response.writeHead(404, {
        'Content-Type': 'text/plain',
      });
      response.end('Document not found');

      return Promise.reject(error.message);
    }

    if (!docAsBuffer) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'CoverpathBaseAccountCenterController',
          function: 'onContentRequestHandler',

          category: Category.request,
        },
        `Cannot find document for customer`
      );

      response.writeHead(404, {
        'Content-Type': 'text/plain',
      });
      response.end('Document not found');

      return Promise.resolve();
    }

    response.writeHead(200, {
      'Content-Type': 'application/pdf',
      'Content-Disposition': `inline; filename=${fileName}`,
      'Content-Length': docAsBuffer.length,
    });

    const bufferStream = new PassThrough();
    bufferStream.end(docAsBuffer);
    bufferStream.pipe(response);
  }

  @remote({ on: 'server' })
  async getAnnuityAppDocuments(): Promise<AnnuityAppDocument[]> {
    const annuity = this.getCustomer().application.primaryPolicy as CoverpathAnnuity;
    const policyDocs = await CoverpathUtilsBackEnd.fetchPolicyDocs(annuity);
    const unorderedDocTypes = policyDocs.map((policyDoc) => policyDoc.appType);
    const docTypes = Object.values(POLICY_DOC_ANNUITY_DOCTYPE).filter((docType) => unorderedDocTypes.includes(docType));
    return Promise.all(
      docTypes.map((docType) => AnnuityDocuments.getAnnuityDoc(annuity.product, annuity.getApplicationState(), docType))
    );
  }

  /**
   * Returns the single PolicyDoc of the specified appType, or null if there isn't exactly one
   */
  async getSinglePolicyDoc(policy: Policy, appType: string, name: string): Promise<PolicyDoc> {
    const policyId = policy._id.toString();
    const docQuery = { $and: [{ policyId }, { appType }, { name }, { isActive: 'true' }] };

    const allPolicyDocMatches = await PolicyDoc.persistorFetchByQuery(docQuery, {
      session: this.amorphic,
      transient: true,
    });

    if (allPolicyDocMatches.length !== 1) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'CoverpathBaseAccountCenterController',
          function: 'getSinglePolicyDoc',
          context: {
            piiLevel: UIPiiLevelEnum.s2,
          },
          data: {
            policyNumber: policy.policyNumber,
            appType,
            policyId,
            name,
          },
          category: UICategory.request,
        },
        `Cannot find a single PolicyDoc`
      );

      return null;
    }

    return allPolicyDocMatches[0];
  }

  private async getApplicationPDFAsBuffer(policy: Policy): Promise<Buffer> {
    const policyId = policy._id.toString();

    return PolicyDoc.persistorFetchByQuery(
      (knex) =>
        knex
          .whereIn('appType', ['AppPdf', 'Packet', 'Conversion'])
          .andWhere('policyId', '=', policyId)
          .andWhere('isActive', '=', 'true')
          .orderBy('date', 'desc')
          .limit(1),
      {
        session: this.amorphic,
        transient: true,
      }
    ).then(function (policyDocs) {
      if (policyDocs.length !== 1) {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'CoverpathBaseAccountCenterController',
            function: 'getApplicationPDFAsBuffer',
            category: UICategory.request,
            data: {
              policyNumber: policy.policyNumber,
            },
          },
          `Cannot find a single PolicyDoc for ${policy.policyNumber}`
        );

        return null;
      }

      return Buffer.from(policyDocs[0].docContent.toString(), 'base64');
    });
  }

  @remote({ on: 'server' })
  async checkRequirement(
    policy: Policy,
    requirement: string,
    futureSubState: string,
    futureState?: string
  ): Promise<void> {
    let reqToCheck: string;

    try {
      reqToCheck = _.find(policy.currentStageRequirements, (req) => {
        return req.match(new RegExp(requirement));
      });
      if (!reqToCheck) {
        LoggerSingleton.getInstance().info(
          {
            module: 'accountCenterController.checkRequirement',
            function: 'checkRequirement',

            category: Category.request,
          },
          `Requirement: '${requirement}' not in currentStageRequirements for policyId:'${policy._id}'`
        );
        return;
      }
    } catch (e) {
      let message: string;
      // Logging out a specific issue, detailed in production logs in tickets: HL-8317/HL-9445
      if (!policy) {
        message = `Error Policy is missing while attempting to satisfy requirement: '${requirement}', ${e}`;
      } else {
        message = `Error while attempting to satisfy requirement: '${requirement}' policyId:'${policy._id}', ${e}`;
      }
      const error = {
        isHumanRelated: false,
        name: e.name,
        message: e.message,
        stack: e.stack,
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'accountCenterController.checkRequirement',
          function: 'checkRequirement',

          category: Category.request,
          error: error,
        },
        message
      );
      return;
    }

    let force = false;
    switch (reqToCheck) {
      case 'Accept':
        LoggerSingleton.getInstance().warn(
          {
            module: 'accountCenterController.checkRequirement',
            function: 'checkRequirement',

            category: Category.request,
          },
          "Cannot 'Accept' Policy"
        );
        return;

      case 'Resend Application to Docusign':
        force = true;
        break;
    }

    //Note: fulfillReq is async
    return policy.fulfillReq(reqToCheck, futureSubState, futureState, force);
  }

  @remote({ on: 'server' })
  async deleteExistingRBIIfExists(): Promise<void> {
    const policy = this.getCustomer().application.primaryPolicy;

    if (policy.typeWithPendingAmendment() === POLICY_TYPES.AMEND_REMOVE_PRIMARY) {
      const txn = this.getController().amorphic.begin();
      await RevisedBasicIllustrationUtils.deleteExistingRBIIfExists(
        policy,
        this.getCustomer().primaryCustomer.party,
        txn,
        this.amorphic['incomingIP']
      );
      await this.getController().amorphic.end(txn);
    } else {
      LoggerSingleton.getInstance().warn(
        {
          module: 'AccountCenterController',
          function: 'deleteExistingRBIIfExists',

          category: Category.request,
        },
        `Trying to delete RBI for WL Policy which is not removed by amendments`
      );
    }
  }

  @remote({ on: 'server' })
  async customerNeedsToSignRevisedBasicIllustration(policyNumber: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber) as CoverpathPolicy;
    if (!policy.application.primaryPolicyWithPendingAmendment.isWholeLifePolicyWithPendingAmendment()) {
      return false;
    }

    const persistedPolicyDocs = await PolicyDoc.persistorFetchByQuery(
      {
        policyId: policy.application.primaryPolicy._id,
        appType: POLICY_DOC_DOCTYPE.RevisedBasicIllustration,
        isActive: 'true',
      },
      {
        session: this.amorphic,
      }
    );

    return persistedPolicyDocs.length > 0;
  }

  @remote({ on: 'server' })
  async customerNeedsToSignInsurabilityForm(policyNumber: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber);

    if (!CoverpathUtils.isInsurabilityStatementSignatureRequired(policy)) {
      await this.deleteInsurabilityDocIfExist(policyNumber);
      return false;
    }

    await this.generateAndPersistInsurabilityForm(policy as CoverpathPolicy);
    return true;
  }

  private async generateAndPersistInsurabilityForm(policy: CoverpathPolicy) {
    const pdfController = PdfController.new({
      customer: this.getCustomer(),
      loggedInRole: 'producer',
    });
    const formData = CoverpathUtils.getCoverpathInsurabilityFormData(policy);
    const persistedPolicyDocs = await PolicyDoc.persistorFetchByQuery(
      {
        policyId: policy._id,
        name: formData.templateName.replace('.pdf', ''),
        isActive: 'true',
      },
      {
        session: this.amorphic,
      }
    );

    if (persistedPolicyDocs.length > 1) {
      throw Error(
        `Policy ${policy.policyNumber} has more than 1 insurability doc already; cannot determine which one to update`
      );
    }

    if (persistedPolicyDocs.length === 0) {
      pdfController.docType = 'insurability_statement';
      const forms = await pdfController.fillForms([formData], false);
      const filledInsurabilityForm = forms[0];
      //Setting docType to `Amendment` even though it's insurability form
      //This is for policy admin to add this to policy packet
      //It will be `InsurabilityStatement` when policy admin supports it
      const policyDoc = PolicyDoc.new(
        filledInsurabilityForm.name.replace('.pdf', ''),
        filledInsurabilityForm.body,
        POLICY_DOC_TYPES.pdf,
        POLICY_DOC_SOURCES.app,
        policy,
        'Amendment'
      );
      await policyDoc.setDirty(this.getAppLevelAmorphicTransaction());

      await PolicyDocAction.newWithProps(
        policyDoc,
        this.getCustomer().primaryCustomer.party,
        POLICY_DOC_ACTION_TYPE.CREATED,
        this.amorphic['incomingIP']
      ).setDirty(this.getAppLevelAmorphicTransaction());
    }
  }

  @remote({ on: 'server' })
  async customerNeedsToSignBasicIllustration(doc: Buffer): Promise<boolean> {
    await this.persistBasicIllustration(doc);
    return true;
  }

  private async persistBasicIllustration(doc: Buffer) {
    const persistedPolicyDocs = await PolicyDoc.persistorFetchByQuery(
      {
        policyId: this.getCustomer().application.primaryPolicy._id,
        appType: 'BasicIllustration',
        isActive: 'true',
      },
      {
        session: this.amorphic,
      }
    );

    if (persistedPolicyDocs.length > 1) {
      throw Error(
        `Policy ${
          this.getCustomer().application.primaryPolicy.policyNumber
        } has more than 1 BI doc already; cannot determine which one to update`
      );
    } else {
      await this.deletePolicyDocIfExists(
        this.getCustomer().application.primaryPolicy.policyNumber,
        'BasicIllustration'
      );
      const policyDoc = PolicyDoc.new(
        'BasicIllustration',
        doc,
        POLICY_DOC_TYPES.pdf,
        POLICY_DOC_SOURCES.app,
        this.getCustomer().application.primaryPolicy,
        'BasicIllustration'
      );

      policyDoc.setDirty(this.getAppLevelAmorphicTransaction());

      PolicyDocAction.newWithProps(
        policyDoc,
        this.getCustomer().primaryCustomer.party,
        POLICY_DOC_ACTION_TYPE.CREATED,
        this.amorphic['incomingIP']
      ).setDirty(this.getAppLevelAmorphicTransaction());
    }
  }

  @remote({ on: 'server' })
  async customerNeedsToSignConversionForm(
    policy: CoverpathPolicy,
    floridaStateLicense?: string,
    agentCountyCityStateString?: string
  ): Promise<boolean> {
    await this.generateAndPersistConversionForm(
      policy as CoverpathPolicy,
      floridaStateLicense,
      agentCountyCityStateString
    );
    return true;
  }

  private async generateAndPersistConversionForm(
    policy: CoverpathPolicy,
    floridaStateLicense?: string,
    agentCountyCityStateString?: string
  ) {
    const pdfController = PdfController.new({
      customer: this.getCustomer(),
      loggedInRole: 'producer',
    });
    const formData = CoverpathUtils.getCoverpathConversionFormData(
      policy,
      floridaStateLicense,
      agentCountyCityStateString
    );
    const persistedPolicyDocs = await PolicyDoc.persistorFetchByQuery(
      {
        policyId: policy._id,
        name: formData.templateName.replace('.pdf', ''),
        isActive: 'true',
      },
      {
        session: this.amorphic,
      }
    );

    if (persistedPolicyDocs.length > 1) {
      throw Error(
        `Policy ${policy.policyNumber} has more than 1 simplified conversion doc already; cannot determine which one to update`
      );
    } else {
      await this.deletePolicyDocIfExists(policy.policyNumber, 'Conversion');
      pdfController.docType = 'conversion';
      const forms = await pdfController.fillForms([formData], false);
      const filledConversionForm = forms[0];
      const policyDoc = PolicyDoc.new(
        filledConversionForm.name.replace('.pdf', ''),
        filledConversionForm.body,
        POLICY_DOC_TYPES.pdf,
        POLICY_DOC_SOURCES.app,
        policy,
        'Conversion'
      );
      policyDoc.setDirty(this.getAppLevelAmorphicTransaction());

      PolicyDocAction.newWithProps(
        policyDoc,
        this.getCustomer().primaryCustomer.party,
        POLICY_DOC_ACTION_TYPE.CREATED,
        this.amorphic['incomingIP']
      ).setDirty(this.getAppLevelAmorphicTransaction());
    }
  }

  @remote({ on: 'server' })
  async customerNeedsToSignPappForm(policyNumber: string, SSN: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber);

    await this.generateAndPersistPappForm(policy as CoverpathPolicy, SSN);
    return true;
  }

  private async generateAndPersistPappForm(policy: CoverpathPolicy, SSN: string) {
    const pdfController = PdfController.new({
      customer: this.getCustomer(),
      loggedInRole: 'producer',
    });
    // paymentDay should be the current day or 28, if the date is > 28
    const day = new Date().getDate();
    policy.paymentDay = day > 28 ? 28 : day;
    const formData = CoverpathUtils.getPappFormData(policy, SSN);
    const persistedPolicyDocs = await PolicyDoc.persistorFetchByQuery(
      {
        policyId: policy._id,
        name: formData.templateName.replace('.pdf', ''),
        isActive: 'true',
      },
      {
        session: this.amorphic,
      }
    );

    if (persistedPolicyDocs.length > 1) {
      throw Error(
        `Policy ${policy.policyNumber} has more than 1 Papp doc already; cannot determine which one to update`
      );
    } else {
      await this.deletePolicyDocIfExists(policy.policyNumber, 'Papp');
      pdfController.docType = 'Papp';
      const forms = await pdfController.fillForms([formData], false);
      const filledPappForm = forms[0];
      const policyDoc = PolicyDoc.new(
        filledPappForm.name.replace('.pdf', ''),
        filledPappForm.body,
        POLICY_DOC_TYPES.pdf,
        POLICY_DOC_SOURCES.app,
        policy,
        'Papp'
      );
      policyDoc.setDirty(this.getAppLevelAmorphicTransaction());

      PolicyDocAction.newWithProps(
        policyDoc,
        this.getCustomer().primaryCustomer.party,
        POLICY_DOC_ACTION_TYPE.CREATED,
        this.amorphic['incomingIP']
      ).setDirty(this.getAppLevelAmorphicTransaction());
    }
  }

  @remote({ on: 'server' })
  async customerNeedsToSignConsentForm(policyNumber: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber);

    await this.generateAndPersistConsentForm(policy as CoverpathPolicy);
    return true;
  }

  private async generateAndPersistConsentForm(policy: CoverpathPolicy) {
    const pdfController = PdfController.new({
      customer: this.getCustomer(),
      loggedInRole: 'producer',
    });
    const formData = CoverpathUtils.getConsentFormData(policy);
    const persistedPolicyDocs = await PolicyDoc.persistorFetchByQuery(
      {
        policyId: policy._id,
        name: formData.templateName.replace('.pdf', ''),
        isActive: 'true',
      },
      {
        session: this.amorphic,
      }
    );

    if (persistedPolicyDocs.length > 1) {
      throw Error(
        `Policy ${policy.policyNumber} has more than 1 consent doc already; cannot determine which one to update`
      );
    } else {
      await this.deletePolicyDocIfExists(policy.policyNumber, 'Consent');
      pdfController.docType = 'Consent';
      const forms = await pdfController.fillForms([formData], false);
      const filledConsentForm = forms[0];
      const policyDoc = PolicyDoc.new(
        filledConsentForm.name.replace('.pdf', ''),
        filledConsentForm.body,
        POLICY_DOC_TYPES.pdf,
        POLICY_DOC_SOURCES.app,
        policy,
        'Consent'
      );
      policyDoc.setDirty(this.getAppLevelAmorphicTransaction());

      PolicyDocAction.newWithProps(
        policyDoc,
        this.getCustomer().primaryCustomer.party,
        POLICY_DOC_ACTION_TYPE.CREATED,
        this.amorphic['incomingIP']
      ).setDirty(this.getAppLevelAmorphicTransaction());
    }
  }

  private async deletePolicyDocIfExists(policyNumber: string, formType: string) {
    const policy = this.getPolicyWithNumber(policyNumber) as CoverpathPolicy;
    let formName;

    if (formType === 'BasicIllustration') {
      formName = 'BasicIllustration';
    } else {
      const formDataFunc = CoverpathUtils.getFormDataByType(formType);
      formName = formDataFunc.apply(CoverpathUtils, [policy]).templateName.replace('.pdf', '');
    }

    const policyDoc = await this.getSinglePolicyDoc(policy, formType, formName);
    if (policyDoc) {
      await policyDoc.persistorDelete();

      await PolicyDocAction.newWithProps(
        policyDoc,
        this.getCustomer().primaryCustomer.party,
        POLICY_DOC_ACTION_TYPE.DELETED,
        this.amorphic['incomingIP']
      ).persistorSave();
    }
  }

  private async generateAndPersistMecPDFIfNeeded(policy: Policy): Promise<PolicyDoc> {
    const mecPolicyDoc = await this.getSinglePolicyDoc(policy, 'Amendment', 'a5030_mec');
    if (!mecPolicyDoc) {
      return this.generateMecForm(policy, this.getCustomer());
    } else {
      return mecPolicyDoc;
    }
  }

  private async generateMecForm(policy: Policy, customer: Customer): Promise<PolicyDoc> {
    let policyDoc: PolicyDoc;
    const formData = CoverpathUtils.getCoverpathMecFormData(policy as CoverpathPolicy);
    const persistedPolicyDocs = await PolicyDoc.persistorFetchByQuery(
      {
        policyId: policy._id,
        name: formData.templateName.replace('.pdf', ''),
        isActive: 'true',
      },
      {
        session: this.amorphic,
      }
    );

    if (persistedPolicyDocs.length > 1) {
      throw Error(
        `Policy ${policy.policyNumber} has more than 1 mec doc already; cannot determine which one to update`
      );
    }

    if (persistedPolicyDocs.length === 0) {
      const pdfController = PdfController.new({
        customer: this.getCustomer(),
        loggedInRole: 'producer',
      });
      formData.mappings['TaxId1'] = await SecurityUtils.getPiiValueByKey(formData.mappings['TaxId1'], 0, true);
      formData.mappings['TaxId2'] = await SecurityUtils.getPiiValueByKey(formData.mappings['TaxId2'], 0, true);
      // for RBI during amendment, mec form pdf is created again
      const forms = await pdfController.fillForms([formData], false);
      const filledMecForm = forms[0];
      policyDoc = PolicyDoc.new(
        filledMecForm.name.replace('.pdf', ''),
        filledMecForm.body,
        POLICY_DOC_TYPES.pdf,
        POLICY_DOC_SOURCES.app,
        policy,
        'Amendment'
      );
      await policyDoc.persistSave();

      await PolicyDocAction.newWithProps(
        policyDoc,
        customer.primaryCustomer.party,
        POLICY_DOC_ACTION_TYPE.CREATED,
        this.amorphic['incomingIP']
      ).persistSave();
    }
    return policyDoc;
  }

  @remote({ on: 'server' })
  async customerNeedsToSignMecForm(policyNumber: string): Promise<boolean> {
    const policy = this.getPolicyWithNumber(policyNumber) as CoverpathPolicy;
    const primaryPolicyWithPendingAmendment = policy.application.primaryPolicyWithPendingAmendment;

    const isPolicyMec: boolean = await RevisedBasicIllustrationUtils.isPolicyMecForRBI(
      primaryPolicyWithPendingAmendment
    );
    const wasPolicyMecInDocusign: boolean = await RevisedBasicIllustrationUtils.isPolicyMecForBI(policy);

    if (isPolicyMec && !wasPolicyMecInDocusign) {
      const mecForm = await this.generateAndPersistMecPDFIfNeeded(policy);
      if (mecForm) {
        LoggerSingleton.getInstance().info(
          {
            module: 'accountCenterController',
            function: 'customerNeedsToSignMecForm',
            data: {
              policyNumber: policy.policyNumber,
            },
            category: Category.request,
          },
          `MEC form will need to be signed since the amended values make the policy MEC and we did not find any MEC form in the database`
        );
        return true;
      }
    }
    return false;
  }

  private getInsurabilityFormSignatureInput(policy: Policy) {
    return {
      data: [
        {
          formType: 'pdfDraw',
          fills: [
            {
              page: 1,
              fill: [
                {
                  _comment: 'client signature',
                  type: 'signature',
                  text: policy.getOwnerSigningName(),
                  x: 120,
                  y: 108,
                  fontSize: 16,
                },
                {
                  _comment: 'client signature date',
                  type: 'date',
                  x: 460,
                  y: 108,
                },
              ],
            },
          ],
          buffer: null,
        },
      ],
    };
  }

  private getMecFormSignatureInput(policy: Policy) {
    return {
      data: [
        {
          formType: 'pdfDraw',
          fills: [
            {
              page: 1,
              fill: [
                {
                  _comment: 'client signature',
                  type: 'signature',
                  text: policy.getOwnerSigningName(),
                  x: 250,
                  y: 325,
                  fontSize: 16,
                },
                {
                  _comment: 'client signature date',
                  type: 'date',
                  x: 480,
                  y: 325,
                },
              ],
            },
          ],
          buffer: null,
        },
      ],
    };
  }

  private getAmendmentFormSignatureInput(policy: Policy) {
    const applicationState = policy.getApplicationState();

    let nameYCoordinate;
    let dateYCoordinate;

    switch (applicationState) {
      case 'DE':
      case 'ND':
      case 'SD':
        nameYCoordinate = 180;
        dateYCoordinate = 160;
        break;
      case 'FL':
        nameYCoordinate = 155;
        dateYCoordinate = 135;
        break;
      case 'CA':
        nameYCoordinate = 180;
        dateYCoordinate = 160;
        break;
      default:
        nameYCoordinate = 175;
        dateYCoordinate = 155;
    }

    return {
      data: [
        {
          formType: 'pdfDraw',
          fills: [
            {
              page: 1,
              fill: [
                {
                  _comment: 'client signature',
                  type: 'signature',
                  text: policy.getOwnerSigningName(),
                  x: 120,
                  y: nameYCoordinate,
                  fontSize: 16,
                },
                {
                  _comment: 'client signature date',
                  type: 'date',
                  x: 460,
                  y: dateYCoordinate,
                },
              ],
            },
          ],
          buffer: null,
        },
      ],
    };
  }

  private getRevisedBasicIllustrationSignatureInput(policy: Policy) {
    return {
      data: [
        {
          formType: 'pdfDraw',
          fills: [
            {
              page: 6,
              fill: [
                {
                  _comment: 'client signature',
                  type: 'signature',
                  text: policy.getOwnerSigningName(),
                  x: 25,
                  y: 250,
                  fontSize: 16,
                },
                {
                  type: 'date',
                  text: '',
                  x: 300,
                  y: 250,
                },
              ],
            },
          ],
          buffer: '',
        },
      ],
    };
  }

  private getConversionFormSignatureInput(policy: Policy) {
    const signatureYCoordinate = policy.getApplicationState() === 'FL' ? 542 : 552;

    return {
      data: [
        {
          formType: 'pdfDraw',
          fills: [
            {
              page: 2,
              fill: [
                {
                  _comment: 'client signature',
                  type: 'signature',
                  text: policy.getOwnerSigningName(),
                  x: 200,
                  y: signatureYCoordinate,
                  fontSize: 16,
                },
              ],
            },
          ],
          buffer: null,
        },
      ],
    };
  }

  private getPappFormSignatureInput(policy: Policy) {
    return {
      data: [
        {
          formType: 'pdfDraw',
          fills: [
            {
              page: 1,
              fill: [
                {
                  _comment: 'client signature',
                  type: 'signature',
                  text: policy.getOwnerSigningName(),
                  x: 236,
                  y: 143,
                  fontSize: 16,
                },
              ],
            },
          ],
          buffer: null,
        },
      ],
    };
  }

  private getConsentFormSignatureInput(policy: Policy) {
    let signatureXCoordinate;
    let signatureYCoordinate;

    if (policy.getApplicationState() === 'CA') {
      signatureXCoordinate = 133;
      signatureYCoordinate = 333;
    } else {
      signatureXCoordinate = 243;
      signatureYCoordinate = 231;
    }
    return {
      data: [
        {
          formType: 'pdfDraw',
          fills: [
            {
              page: 1,
              fill: [
                {
                  _comment: 'client signature',
                  type: 'signature',
                  text: policy.getOwnerSigningName(),
                  x: signatureXCoordinate,
                  y: signatureYCoordinate,
                  fontSize: 16,
                },
              ],
            },
          ],
          buffer: null,
        },
      ],
    };
  }

  @remote({ on: 'server' })
  async getAnnuityStaticDocsWithPolicy(
    annuity: CoverpathAnnuity
  ): Promise<Array<{ displayName: string; pdfUrl: string }>> {
    const policyDocs = await CoverpathUtilsBackEnd.fetchPolicyDocs(annuity);
    const state = annuity.getApplicationState();
    const product = (this.getCustomer().application.primaryPolicy as CoverpathAnnuity).product;
    const docs = AnnuityDocuments.fetchDocsForClientEsign(policyDocs, product, state, this.getPath());
    return docs;
  }

  @remote({ on: 'server' })
  async getAnnuityStaticDocs(): Promise<Array<{ displayName: string; pdfUrl: string }>> {
    const annuity = this.getCustomer().application.primaryPolicy as CoverpathAnnuity;
    return this.getAnnuityStaticDocsWithPolicy(annuity);
  }

  @remote({ on: 'server' })
  async hasPolicyDoc(docType: string): Promise<boolean> {
    const policyDoc = await CoverpathUtilsBackEnd.fetchSinglePolicyDoc(
      this.getCustomer().application.primaryPolicy,
      docType
    );
    return !!policyDoc;
  }

  @remote({ on: 'server' })
  async getAndSignForm(policyNumber: string, formType: string) {
    const policy = this.getPolicyWithNumber(policyNumber);

    LoggerSingleton.getInstance().info(
      {
        module: 'accountCenterController',
        function: 'getAndSignForm',
        category: Category.request,
        data: {
          policyNumber: policy.policyNumber,
          formType,
        },
      },
      `Customer is signing`
    );

    const file = pathResolve(__dirname, '../../../common/js/metadata/data/esignfields/fields_insured.json');
    const fieldsInsured = JSON.parse(String(fs.readFileSync(file)));
    let formSignatureInput;
    let formName: string = '';
    let appType: string = '';
    if (formType === 'Amendment') {
      formSignatureInput = this.getAmendmentFormSignatureInput(policy);
      appType = POLICY_DOC_DOCTYPE.amendment;
      formName = CoverpathUtils.getAmendmentFormNameByState(policy.getApplicationState());
      if (policy.isOwnerSameAsInsuredOrJuvenile()) {
        fieldsInsured[formName].fills[0].fill[0].text = (policy as CoverpathBasePolicy).getSignerFullName();
        formSignatureInput.data[0].fills[0].fill = formSignatureInput.data[0].fills[0].fill.concat(
          fieldsInsured[formName].fills[0].fill
        );
      }
    } else if (formType === 'Insurability') {
      formSignatureInput = this.getInsurabilityFormSignatureInput(policy);

      //Setting docType to `Amendment` even though it's insurability form
      //This is for policy admin to add this to policy packet
      //It will be `InsurabilityStatement` when policy admin supports it
      appType = POLICY_DOC_DOCTYPE.amendment;
      formName = CoverpathUtils.getInsurabilityFormNameByState(policy.getApplicationState());
      if (policy.isOwnerSameAsInsuredOrJuvenile()) {
        fieldsInsured['ISDTC'].fills[0].fill[0].text = (policy as CoverpathBasePolicy).getSignerFullName();
        formSignatureInput.data[0].fills[0].fill = formSignatureInput.data[0].fills[0].fill.concat(
          fieldsInsured['ISDTC'].fills[0].fill
        );
      }
    } else if (formType === 'RevisedBasicIllustration') {
      formSignatureInput = this.getRevisedBasicIllustrationSignatureInput(policy);
      const illustrationSignPage = await this.getSignaturePageNumber(policy);
      formSignatureInput.data[0].fills[0].page = illustrationSignPage;
      appType = POLICY_DOC_DOCTYPE.RevisedBasicIllustration;
      formName = POLICY_DOC_DOCTYPE.RevisedBasicIllustration;
    } else if (formType === POLICY_DOC_DOCTYPE.mec) {
      formSignatureInput = this.getMecFormSignatureInput(policy);
      appType = POLICY_DOC_DOCTYPE.amendment;
      formName = CoverpathUtils.getCoverpathMecFormData(policy as CoverpathPolicy).templateName.replace('.pdf', '');
    } else if (formType === POLICY_DOC_DOCTYPE.conversion) {
      formSignatureInput = this.getConversionFormSignatureInput(policy);
      appType = POLICY_DOC_DOCTYPE.conversion;
      formName = CoverpathUtils.getCoverpathConversionFormData(policy as CoverpathPolicy).templateName.replace(
        '.pdf',
        ''
      );
    } else if (formType === POLICY_DOC_DOCTYPE.papp) {
      formSignatureInput = this.getPappFormSignatureInput(policy);
      appType = POLICY_DOC_DOCTYPE.papp;
      formName = CoverpathUtils.getPappFormData(policy as CoverpathPolicy).templateName.replace('.pdf', '');
    } else if (formType === POLICY_DOC_DOCTYPE.consent) {
      formSignatureInput = this.getConsentFormSignatureInput(policy);
      appType = POLICY_DOC_DOCTYPE.consent;
      formName = CoverpathUtils.getConsentFormData(policy as CoverpathPolicy).templateName.replace('.pdf', '');
    } else if (formType === 'BasicIllustration') {
      const convertedFromSanderling = CoverpathUtils.convertedFromSanderling(policy);
      formSignatureInput = this.getRevisedBasicIllustrationSignatureInput(policy);
      const illustrationSignPage = await this.getSignaturePageNumber(policy, convertedFromSanderling);
      formSignatureInput.data[0].fills[0].page = illustrationSignPage;
      appType = POLICY_DOC_DOCTYPE.basicIllustration;
      formName = POLICY_DOC_DOCTYPE.basicIllustration;
    } else {
      const error = {
        isHumanRelated: false,
        data: {
          policyNumber: policy.policyNumber,
          formType,
        },
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'accountCenterController',
          function: 'getAndSignForm',
          category: Category.request,
          error: error,
        },
        `formType is invalid or undefined`
      );
    }

    await this.signAndPersistPdf(policy, appType, formName, formSignatureInput);
  }

  private async signAndPersistPdf(
    policy: Policy,
    appType: string,
    name: string,
    formSignatureInput: PDFMappingDataObject
  ) {
    const existingPDFDoc = await this.getSinglePolicyDoc(policy, appType, name);
    if (!existingPDFDoc) {
      throw Error(`Could not find the existing ${appType} PDF to sign`);
    }

    formSignatureInput.data[0].buffer = Buffer.from(existingPDFDoc.docContent.toString(), 'base64');

    const pdfController = PdfController.new({
      customer: this.getCustomer(),
      loggedInRole: 'producer',
    });
    const pdfStreamOptions = {
      url: `${pdfController.getPdfStreamUrl()}/fill/CAS?isFinal=true`,
      json: true,
      body: formSignatureInput,
      method: 'POST',
      headers: pdfController.getHeaders(),
    };

    const signedPdf = await rp(pdfStreamOptions);
    UILoggerSingleton.getInstance().info(
      {
        module: 'accountCenterController',
        function: 'signAndPersistPdf',
        data: {
          policyNumber: policy.policyNumber,
          appType,
        },
        category: UICategory.request,
      },
      `Generated signed PDF; will persist it`
    );

    existingPDFDoc.docContent = signedPdf;
    existingPDFDoc.date = new Date();
    existingPDFDoc.setDirty(this.getAppLevelAmorphicTransaction());

    PolicyDocAction.newWithProps(
      existingPDFDoc,
      policy.owner,
      POLICY_DOC_ACTION_TYPE.SIGNED,
      this.amorphic['incomingIP']
    ).setDirty(this.getAppLevelAmorphicTransaction());
  }

  @remote({ on: 'server' })
  async signAnnuityForm(
    policy: CoverpathPolicy,
    appType: string,
    signerName: string,
    additionalSigner?: AnnuityAdditionalSigners
  ) {
    LoggerSingleton.getInstance().info(
      {
        module: 'accountCenterController',
        function: 'signAnnuityForm',
        data: {
          policyNumber: policy.policyNumber,
          appType,
        },
        category: Category.request,
      },
      `${signerName} is signing`
    );

    const formSignatureInput = await AnnuityPDFUtils.getClientSignatureInput(
      policy as CoverpathAnnuity,
      appType,
      signerName,
      additionalSigner
    );

    await this.signAndPersistPdf(policy, appType, appType, formSignatureInput);
  }

  private async deleteInsurabilityDocIfExist(policyNumber: string) {
    const policy = this.getPolicyWithNumber(policyNumber) as CoverpathPolicy;

    const formName = CoverpathUtils.getCoverpathInsurabilityFormData(policy).templateName.replace('.pdf', '');
    const insurabilityPolicyDoc = await this.getSinglePolicyDoc(policy, 'Amendment', formName);
    if (insurabilityPolicyDoc) {
      await insurabilityPolicyDoc.persistorDelete();

      await PolicyDocAction.newWithProps(
        insurabilityPolicyDoc,
        this.getCustomer().primaryCustomer.party,
        POLICY_DOC_ACTION_TYPE.DELETED,
        this.amorphic['incomingIP']
      ).persistorSave();
    }
  }

  @remote({ on: 'server' })
  applyUpdatedPaymentInformationToPolicy(policyNumber: string) {
    const policy = this.getPolicyWithNumber(policyNumber);
    policy.paymentBankName = this.paymentInformation.paymentBankName;
    policy.paymentAccountType = this.paymentInformation.paymentAccountType;
    policy.paymentABA = this.paymentInformation.paymentABA;
    policy.paymentAccount = this.paymentInformation.paymentAccount;
    policy.application.primaryPolicyWithPendingAmendment.isAutoPay = this.paymentInformation.isAutoPay;
    if (policy.application.secondaryPolicyWithPendingAmendment) {
      policy.application.secondaryPolicyWithPendingAmendment.isAutoPay = this.paymentInformation.isAutoPay;
    }
    policy.giactWebDebitResponseCode = this.giactWebDebitResponseCode;
    policy.isGiactWebDebitValid = this.isGiactWebDebitValid;
    policy.lastWebDebitAttemptCombination = this.lastWebDebitAttemptCombination;
    if (policy.convertedFrom) {
      policy.paymentMode = ConversionPaymentMode.ACH;
    }
  }

  @remote({ on: 'server' })
  async resendVerificationEmail(): Promise<void> {
    if (this.getController().isAuth0Enabled) {
      await this.getAMCController().resendAMCVerificationEmail();
    } else {
      const customer = this.getCustomer();
      await customer.setEmailVerificationCode();

      this.getController().sendEmail('register_verify', customer.email, customer.firstName + ' ' + customer.lastName, [
        { name: 'firstName', content: customer.firstName },
        { name: 'email', content: customer.email },
        { name: 'verificationCode', content: customer.validateEmailCode },
      ]);
    }
  }

  @remote({ on: 'server' })
  async cloneApplicationForReapply(): Promise<boolean> {
    const customer = this.getCustomer();

    // cloneApplicationForReapply will produce unknown result, if customer.application attribute is not defined
    if (!customer.application) {
      const error = {
        isHumanRelated: false,
        data: {
          ...Utils.extractIds(customer),
        },
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'CoverpathBaseAccountCenterController',
          function: 'cloneApplicationForReapply',
          category: Category.request,

          error: error,
        },
        'Customer Application is missing'
      );

      return false;
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'CoverpathBaseAccountCenterController',
        function: 'cloneApplicationForReapply',

        data: Utils.extractIds(customer, customer.application),
        category: Category.request,
      },
      'Customer requesting a reapply after cancelation'
    );

    const policyObjectWasCanceledDeclinedRejected = customer.application.primaryPolicy.wasCanceledDeclinedRejected();
    const policyAdminStatusWasCanceledDeclinedRejected = await this.wasPACanceledDeclinedRejected(
      customer.application.primaryPolicy.policyNumber
    );

    if (!policyObjectWasCanceledDeclinedRejected && !policyAdminStatusWasCanceledDeclinedRejected) {
      const error = {
        isHumanRelated: false,
        data: {
          ...Utils.extractIds(customer, customer.application),
          policyObjectWasCanceledDeclinedRejected,
          policyAdminStatusWasCanceledDeclinedRejected,
        },
      };
      LoggerSingleton.getInstance().error({
        module: 'CoverpathBaseAccountCenterController',
        function: 'cloneApplicationForReapply',
        category: Category.request,
        error: error,
      });

      return false;
    }

    try {
      // Policy entity fetching deletes any non-persisted change
      // so it should be done before any expression that modify data
      const policies: (MMDirectPolicy | CoverpathPolicy)[] = customer.application.policies;
      await Promise.all(policies.map((policy) => policy.fetch({})));
      await customer.application.cloneApplicationInSession(
        ApplicationSourceType.REAPPLY,
        Assumptions.progress.ApplicationFill
      );
      await this.checkAndUpdateLegacyApp(customer);
      return true;
    } catch (err) {
      const error = {
        isHumanRelated: false,
        data: {
          ...Utils.extractIds(customer, customer.application),
        },
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'CoverpathBaseAccountCenterController',
          function: 'cloneApplicationForReapply',
          error: error,

          category: Category.request,
        },
        'Failed to cancel and create new policy'
      );

      return false;
    }
  }

  @serverOnly()
  async checkAndUpdateLegacyApp(customerLocal: Customer): Promise<Customer> {
    const metadataVersion = customerLocal.application.primaryPolicy.appMetadataVersion;
    const currentMetadataVersion = parseFloat(await MetadataApi.getLatestVersion('App_Questions'));

    if (metadataVersion < currentMetadataVersion) {
      LoggerSingleton.getInstance().info(
        {
          module: 'accountCenterController',
          function: 'publicCancelAndCreateNewPolicy',

          data: {
            subactivity: 'checkAndUpdateLegacyApp',
            metadataVersion: metadataVersion,
          },
          category: Category.request,
        },
        'customer attempting to cancel/reapply with legacy app, updating appMetadataVersion'
      );
      customerLocal.application.primaryPolicy.appMetadataVersion = currentMetadataVersion;
    }
    return customerLocal;
  }

  @remote({ on: 'server' })
  getEncodedApplicationId() {
    return Utils.base64(this.getCustomer().application._id, true);
  }

  abstract getInitialInvoiceInfo(policyNumber: string): Promise<InitialInvoice>;

  @remote({ on: 'server' })
  async resendInsuredEsignEmail(policy): Promise<void> {
    const linkGeneratedAt = Utils.now();
    // set the data
    const signer = policy.insured.getInsuredSigner();
    if (!signer.esignMetadata) {
      signer.esignMetadata = ESignSessionMetadata.new();
    }
    CoverpathUtils.setSignerLinkTimestamp(signer, linkGeneratedAt);
    await CoverpathWorkflowEmails.EmailInsuredForEsign(policy, linkGeneratedAt);
  }

  @remote({ on: 'server' })
  async notifyAdvisorOnceClientSignsConversion(policy: CoverpathPolicy) {
    const templateName = 'notify-advisor-once-client-esigns';

    LoggerSingleton.getInstance().info(
      {
        module: 'AccountCenterController',
        function: 'NotifyAdvisorOnceClientSignsConversion',
        data: {
          policyNumber: policy.policyNumber,
          templateName,
        },
        category: Category.request,
      },
      `Notifying advisor that theclient has esigned the conversion documents`
    );

    return CoverpathWorkflowEmails.NotifyAdvisorOnceClientSignsConversion(policy as CoverpathPolicy);
  }

  @remote({ on: 'server' })
  async getCashValue(policy: Policy): Promise<CashValueQuote> {
    const cashValueQuote: CashValueQuote[] = await PolicyAdminClient.getCashValueInfo([policy]);

    return cashValueQuote && cashValueQuote.length === 1 ? cashValueQuote[0] : null;
  }

  private async getSignaturePageNumber(primaryPolicy, convertedFromSanderling?) {
    const waiverUnits = primaryPolicy.waiver ? primaryPolicy.faceAmount : 0;
    const alirRider: PolicyRider = primaryPolicy.getRider(RiderType.ALIR);
    const exchange1035ForAlir: Exchange1035 = alirRider && alirRider.exchange1035;
    let cashValueTransfer = 0;
    const isMec = await RevisedBasicIllustrationUtils.isPolicyMecForBI(primaryPolicy, convertedFromSanderling);
    if (alirRider && exchange1035ForAlir) {
      cashValueTransfer = exchange1035ForAlir.cashValueTransfer;
    }

    return HtmlPdfUtils.getSignaturePageNumber(
      primaryPolicy.riders,
      primaryPolicy.dividendOption,
      waiverUnits,
      cashValueTransfer,
      isMec
    );
  }

  async getDocusignSubmissionEnvIdLocal(policy: Policy): Promise<string> {
    let envelopeId = null;
    const query = `select i."endpointName", c."refId"
            from interaction i
            join conversation c on c._id = i.conversation_id
            join execution e on e."interactionId" = i._id
            join workflow w on w."_id" = e.workflow_id
            join application a on w."referenceId" = a._id
            where w."referenceType" = 'ApplicationId' and a."_id" = ? and
              (i."endpointName" = 'SendApplicationToDocuSign' or i."endpointName" = 'NotifiedApplicationSigned')`;
    const interactions = await Application.getKnex().client.raw(query, [policy.application._id]);
    if (interactions.rows && interactions.rows.length) {
      const alreadyNotified = interactions.rows.find((row) => row.endpointName === 'NotifiedApplicationSigned');
      if (!alreadyNotified) {
        envelopeId = interactions.rows[0].refId;
      }
    }
    return envelopeId;
  }

  @remote({ on: 'server' })
  async getTeleCMIStatus(application: CasApplication): Promise<string> {
    if (!application.isTeleCMICompleted) {
      return TELE_CASE_STATUS.ACTIVE;
    }
    const policyId = application.primaryPolicy._id;
    const resultSet = await Policy.getKnex().client.raw(
      `select state from workflow_state_history wsh where wsh."policy_id" =?`,
      [policyId]
    );
    if (resultSet.rows && resultSet.rows.length) {
      const isTeleCMIClosed = resultSet.rows.find((row) => row.state === 'License-verification');
      if (isTeleCMIClosed) {
        return TELE_CASE_STATUS.CLOSED;
      }
    }
    return TELE_CASE_STATUS.COMPLETED;
  }

  @remote({ on: 'server' })
  async submitDocusignNotifyLocal(policy: Policy): Promise<void> {
    const envelopeId = await this.getDocusignSubmissionEnvIdLocal(policy);

    if (!envelopeId) {
      return;
    }

    const filePath = pathResolve(__dirname, '../../../../apps/common/mocks/NotifySignedFakeLocal.xml');
    let mockFile = await CommonUtilsBackend.readFile(filePath);
    mockFile = mockFile.toString().replace('REPLACE_ME_ENVELOPE_UUID', envelopeId);
    const notifyWebserviceOfDocusignLocalUrl = 'http://cp-webservice:8005/DocuSignConnectListener/cp';
    const webServiceName = 'NotifiedApplicationSigned';
    const svcConfig = amorphicStatic.config.configStore.webservice.get('wsclients')[webServiceName];
    const secret = svcConfig.WEBHOOK_AUTH_KEY;
    const hmac = createHmac('sha256', secret);
    hmac.write(mockFile, 'utf8');
    hmac.end();
    const signature = hmac.read().toString('base64');
    await rp.post(notifyWebserviceOfDocusignLocalUrl, {
      headers: { 'content-type': 'text/xml', 'x-docusign-signature-1': signature },
      body: mockFile,
    });
    LoggerSingleton.getInstance().info(
      {
        module: 'ApplicationController',
        function: 'submitDocusignNotifyLocal',

        category: Category.request,
      },
      `Submitted mocked xml to notify webservice to pull down DocuSign Forms with envelopeId: ${envelopeId}`
    );
  }

  @remote({ on: 'server' })
  createPolicyIllustrationRequest(policy: CoverpathPolicy, convertedFromSanderling: boolean): IllustrationRequest {
    const agent = policy.primaryPolicyAgent.agent;
    return IllustrationService.createPolicyIllustrationRequestForRBI(agent, policy, convertedFromSanderling);
  }

  @remote({ on: 'server' })
  async getPolicyApplicationId(policyNumber: string): Promise<string> {
    const resultSet = await Policy.getKnex().client.raw(
      `select application_id as id
         from policy
         where "policyNumber" = ?`,
      [policyNumber]
    );
    if (resultSet.rows.length) {
      return Utils.base64(resultSet.rows[0].id, true);
    }
  }

  @remote({ on: 'server' })
  async reapplyApplication(): Promise<ClientReapplyResponseType> {
    const customer = this.getCustomer();
    const primaryPolicy: MMDirectPolicy | CoverpathPolicy = customer.application.primaryPolicy;
    const policyNumber = primaryPolicy.policyNumber;

    LoggerSingleton.getInstance().info(
      {
        module: 'AccountCenterController',
        function: 'Reapply',
        data: {
          policyNumber: policyNumber,
        },
        category: Category.request,
      },
      `Reapply application`
    );

    return ClientReapplyResponseType.SUCCESS;
  }

  @remote({ on: 'server' })
  public async getInsuranceSegmentRiderInfo(policy: Policy): Promise<CACPostRiderDTO> {
    const policyAdminSegmentInfo = await this.getInsuranceSegmentInfo(policy);
    return PolicyAdminRiders.new(policyAdminSegmentInfo).getCACPostRiderInfo();
  }
}
